import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/axios';
import { useAuth } from '@/context/AuthContext';
import {
  ALL_STARTFENSTER_KEYS,
  DEFAULT_BLOCK_ORDER,
  DEFAULT_TILE_ORDER,
  emptyLayout,
  mergeOrder,
  moveItem,
  readLocalLayout,
  writeLocalLayout,
} from '@/pages/Dashboard/startfensterConfig';

function normalizeLayout(data) {
  const base = emptyLayout();
  if (!data || typeof data !== 'object') return base;
  return {
    hidden: [...new Set((data.hidden || []).filter((k) => ALL_STARTFENSTER_KEYS.includes(k)))],
    order: mergeOrder(data.order, DEFAULT_BLOCK_ORDER),
    tileOrder: mergeOrder(data.tileOrder, DEFAULT_TILE_ORDER),
  };
}

/**
 * Lädt/speichert Startfenster-Layout (hidden + Reihenfolgen).
 */
export function useStartfensterLayout() {
  const { user } = useAuth();
  const userId = user?.id;
  const [layout, setLayout] = useState(() => readLocalLayout(userId));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLayout(readLocalLayout(userId));
    api.get('/auth/me/startfenster')
      .then((r) => {
        if (cancelled) return;
        const next = normalizeLayout(r.data);
        setLayout(next);
        writeLocalLayout(userId, next);
      })
      .catch(() => { /* local fallback */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [userId]);

  const hiddenSet = useMemo(() => new Set(layout.hidden), [layout.hidden]);
  const isVisible = useCallback((key) => !hiddenSet.has(key), [hiddenSet]);

  const persist = useCallback(async (next) => {
    const clean = normalizeLayout(next);
    setLayout(clean);
    writeLocalLayout(userId, clean);
    try {
      const r = await api.put('/auth/me/startfenster', clean);
      const saved = normalizeLayout(r.data);
      setLayout(saved);
      writeLocalLayout(userId, saved);
      return saved;
    } catch (e) {
      throw e;
    }
  }, [userId]);

  /** Speichert nur hidden (Dialog Sichtbarkeit); Reihenfolge bleibt. */
  const save = useCallback(async (nextHidden) => {
    const cleanHidden = [...new Set((nextHidden || []).filter((k) => ALL_STARTFENSTER_KEYS.includes(k)))];
    return persist({ ...layout, hidden: cleanHidden });
  }, [layout, persist]);

  const saveOrder = useCallback(async (nextOrder) => {
    return persist({ ...layout, order: mergeOrder(nextOrder, DEFAULT_BLOCK_ORDER) });
  }, [layout, persist]);

  const saveTileOrder = useCallback(async (nextTileOrder) => {
    return persist({ ...layout, tileOrder: mergeOrder(nextTileOrder, DEFAULT_TILE_ORDER) });
  }, [layout, persist]);

  const moveBlock = useCallback(async (fromId, toId) => {
    const next = moveItem(layout.order, fromId, toId);
    return persist({ ...layout, order: next });
  }, [layout, persist]);

  const moveTile = useCallback(async (fromId, toId) => {
    const next = moveItem(layout.tileOrder, fromId, toId);
    return persist({ ...layout, tileOrder: next });
  }, [layout, persist]);

  const resetOrder = useCallback(async () => {
    return persist({
      ...layout,
      order: [...DEFAULT_BLOCK_ORDER],
      tileOrder: [...DEFAULT_TILE_ORDER],
    });
  }, [layout, persist]);

  return {
    layout,
    hidden: layout.hidden,
    order: layout.order,
    tileOrder: layout.tileOrder,
    isVisible,
    save,
    saveOrder,
    saveTileOrder,
    moveBlock,
    moveTile,
    resetOrder,
    loaded,
  };
}
